import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ethers } from 'ethers'

import { zeroAddress } from '../../utils/config'
import ConsoleDialog from './ConsoleDialog'
import { getLocalizedWalletError } from './errorMessages'
import { nftV2Abi } from './nftV2Config'
import { readErc20Metadata } from './tokenMetadata'
import {
    PAYMENT_MODE_ERC20,
    PAYMENT_MODE_NATIVE,
    buildPaymentUpdate,
    formatPaymentAmount,
} from './paymentConfig'

const shortAddress = (address) => address ? `${address.slice(0, 6)}...${address.slice(-4)}` : ''

function paymentLabel(config, nativeSymbol) {
    if (!config) return '-'
    const decimals = config.mode === PAYMENT_MODE_NATIVE ? 18 : (config.token?.decimals ?? 18)
    const symbol = config.mode === PAYMENT_MODE_NATIVE ? nativeSymbol : (config.token?.symbol || shortAddress(config.tokenAddress))
    return `${formatPaymentAmount(config.priceRaw, decimals)} ${symbol}`
}

export default function PaymentConfigPanel({
    beforeSubmit,
    contractAddress,
    currentConfig,
    getProvider,
    nativeSymbol,
    onCopy = () => {},
    onError,
    onUpdated,
    showMessage,
    t,
}) {
    const [showEdit, setShowEdit] = useState(false)
    const [mode, setMode] = useState(PAYMENT_MODE_NATIVE)
    const [tokenAddress, setTokenAddress] = useState('')
    const [token, setToken] = useState(null)
    const [amount, setAmount] = useState('')
    const [tokenLoading, setTokenLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')
    const getProviderRef = useRef(getProvider)
    const onErrorRef = useRef(onError)
    const tokenRequestRef = useRef(0)

    useEffect(() => {
        getProviderRef.current = getProvider
        onErrorRef.current = onError
    }, [getProvider, onError])

    const openEditor = () => {
        const isErc20 = currentConfig.mode === PAYMENT_MODE_ERC20
        setMode(currentConfig.mode)
        setTokenAddress(isErc20 ? currentConfig.tokenAddress : '')
        setToken(isErc20 ? currentConfig.token : null)
        setAmount(formatPaymentAmount(
            currentConfig.priceRaw,
            isErc20 ? (currentConfig.token?.decimals ?? 18) : 18
        ))
        setError('')
        setShowEdit(true)
    }

    const resolveToken = useCallback(async (address, requestId) => {
        try {
            const provider = await getProviderRef.current()
            const metadata = await readErc20Metadata(provider, address)
            if (requestId !== tokenRequestRef.current) return null
            setTokenAddress(metadata.address)
            setToken(metadata)
            return metadata
        } catch (e) {
            if (requestId !== tokenRequestRef.current) return null
            setToken(null)
            setError(e?.code === 'PAYMENT_TOKEN_NOT_CONTRACT'
                ? t('支付代币地址不是合约')
                : t('无法读取支付代币信息'))
            return null
        } finally {
            if (requestId === tokenRequestRef.current) setTokenLoading(false)
        }
    }, [t])

    useEffect(() => {
        const requestId = ++tokenRequestRef.current
        if (!showEdit || mode !== PAYMENT_MODE_ERC20) {
            setTokenLoading(false)
            return undefined
        }

        const address = tokenAddress.trim()
        if (!address) {
            setToken(null)
            setTokenLoading(false)
            setError('')
            return undefined
        }
        if (!ethers.utils.isAddress(address) || address.toLowerCase() === zeroAddress.toLowerCase()) {
            setToken(null)
            setTokenLoading(false)
            setError(address.length >= 42 ? t('请输入有效的支付代币地址') : '')
            return undefined
        }
        if (token?.address?.toLowerCase() === address.toLowerCase()) {
            setTokenLoading(false)
            return undefined
        }

        setToken(null)
        setError('')
        setTokenLoading(true)
        const timer = window.setTimeout(() => resolveToken(address, requestId), 450)
        return () => window.clearTimeout(timer)
    }, [mode, resolveToken, showEdit, t, token?.address, tokenAddress])

    const submit = async () => {
        if (beforeSubmit && beforeSubmit() === false) return
        setSubmitting(true)
        setError('')
        try {
            const metadata = mode === PAYMENT_MODE_ERC20 ? token : null
            if (mode === PAYMENT_MODE_ERC20 && !metadata) {
                setError(tokenLoading ? t('正在自动读取代币信息') : t('请输入有效的支付代币地址'))
                return
            }

            const update = buildPaymentUpdate({
                mode,
                tokenAddress: metadata?.address || zeroAddress,
                amount,
                decimals: metadata?.decimals || 18,
            })
            const provider = await getProviderRef.current()
            const contract = new ethers.Contract(contractAddress, nftV2Abi, provider.getSigner())

            await contract.callStatic.updateMintPayment(update.mode, update.tokenAddress, update.amountRaw)
            const tx = await contract.updateMintPayment(update.mode, update.tokenAddress, update.amountRaw)
            await tx.wait()

            setShowEdit(false)
            showMessage(t('铸造配置修改成功'))
            await onUpdated()
        } catch (e) {
            setError(getLocalizedWalletError(e, t))
            onErrorRef.current(e)
        } finally {
            setSubmitting(false)
        }
    }

    return <>
        <div className="nft-payment-config-card">
            <div className="nft-payment-config-heading">
                <div>
                    <span className="console-overline">{t('支付设置')}</span>
                    <p>{t('铸造配置')}</p>
                </div>
                <button type="button" className="console-button is-secondary" onClick={openEditor}>
                    <i className="fa fa-sliders-h" /> {t('修改铸造配置')}
                </button>
            </div>
            <div className="nft-payment-config-grid">
                <div><span>{t('支付方式')}</span><strong>{currentConfig.mode === PAYMENT_MODE_NATIVE ? nativeSymbol : (currentConfig.token?.symbol || t('其他代币'))}</strong></div>
                <div><span>{t('当前铸造价格')}</span><strong>{paymentLabel(currentConfig, nativeSymbol)}</strong></div>
                {currentConfig.mode === PAYMENT_MODE_ERC20 && <div className="nft-payment-token-row">
                    <span>{t('支付代币')}</span>
                    <button type="button" onClick={() => onCopy(currentConfig.tokenAddress)} aria-label={t('复制支付代币地址')} title={t('复制支付代币地址')}>
                        {currentConfig.token?.symbol || shortAddress(currentConfig.tokenAddress)} · {shortAddress(currentConfig.tokenAddress)} <i className="fa fa-copy" />
                    </button>
                </div>}
            </div>
        </div>

        <ConsoleDialog
            show={showEdit}
            onClose={() => !submitting && setShowEdit(false)}
            title={t('修改铸造配置')}
            description={t('价格与支付资产会原子更新，已打开的铸造页面会要求用户重新确认')}
            icon="fa-coins"
            closeLabel={t('关闭')}
            className="nft-payment-config-modal"
            footer={<>
                <button type="button" className="console-button is-secondary" disabled={submitting} onClick={() => setShowEdit(false)}>{t('取消')}</button>
                <button type="button" className="console-button is-primary" disabled={submitting || !amount || (mode === PAYMENT_MODE_ERC20 && (tokenLoading || !token))} onClick={submit}>
                    {submitting && <i className="fa fa-spinner loading-css" />} {t('提交')}
                </button>
            </>}
        >
                <div className="nft-payment-compare">
                    <span>{t('当前配置')}</span>
                    <strong>{paymentLabel(currentConfig, nativeSymbol)}</strong>
                </div>
                <div className="console-field">
                    <span>{t('支付方式')}</span>
                    <div className="nft-payment-mode-selector">
                        <button type="button" className={mode === PAYMENT_MODE_NATIVE ? 'active' : ''} onClick={() => { setMode(PAYMENT_MODE_NATIVE); setToken(null); setError('') }}>{nativeSymbol}</button>
                        <button type="button" className={mode === PAYMENT_MODE_ERC20 ? 'active' : ''} onClick={() => { setMode(PAYMENT_MODE_ERC20); setError('') }}>{t('其他代币')}</button>
                    </div>
                </div>
                {mode === PAYMENT_MODE_ERC20 && <>
                    <label className="console-field">
                        <span>{t('支付代币地址')}</span>
                        <div className="nft-payment-token-input">
                            <input value={tokenAddress} onChange={(e) => { setTokenAddress(e.target.value.trim()); setToken(null) }} placeholder="0x..." />
                        </div>
                        {!token && !tokenLoading && tokenAddress.length < 42 && <small>{t('输入完整合约地址后将自动识别代币')}</small>}
                    </label>
                    {tokenLoading && <div className="nft-token-resolver is-loading" role="status">
                        <span><i className="fa fa-spinner loading-css" /></span>
                        <div><strong>{t('正在自动读取代币信息')}</strong><small>{t('正在校验合约并读取名称、符号与精度')}</small></div>
                    </div>}
                    {token && <div className="nft-token-identity-card" data-testid="payment-token-card">
                        <div className="nft-token-identity-card__mark">{(token.symbol || 'T').slice(0, 2).toUpperCase()}</div>
                        <div className="nft-token-identity-card__main">
                            <span className="nft-token-identity-card__status"><i /> {t('代币已识别')}</span>
                            <strong>{token.symbol}</strong>
                            <small>{token.name}</small>
                            <code title={token.address}>{token.address}</code>
                        </div>
                        <div className="nft-token-identity-card__meta">
                            <span><small>{t('代币类型')}</small><strong>{t('其他代币')}</strong></span>
                            <span><small>{t('精度')}</small><strong>{token.decimals}</strong></span>
                        </div>
                        <button type="button" onClick={() => onCopy(token.address)} aria-label={t('复制支付代币地址')} title={t('复制支付代币地址')}><i className="fa fa-copy" /></button>
                    </div>}
                    <div className="nft-payment-tax-alert">
                        <i className="fa fa-exclamation-triangle" />
                        <div>{t('含税代币双向免税说明')}</div>
                        <button type="button" onClick={() => onCopy(contractAddress)} aria-label={t('复制合约地址')} title={t('复制合约地址')}>{shortAddress(contractAddress)} <i className="fa fa-copy" /></button>
                    </div>
                </>}
                <label className="console-field">
                    <span>{t('新的单枚铸造价格')}</span>
                    <input value={amount} onChange={(e) => setAmount(e.target.value.trim())} inputMode="decimal" placeholder="0" />
                </label>
                <div className="nft-payment-compare">
                    <span>{t('修改后')}</span>
                    <strong>{amount || '0'} {mode === PAYMENT_MODE_NATIVE ? nativeSymbol : (token?.symbol || t('其他代币'))}</strong>
                </div>
                {error && <div className="console-inline-error"><i className="fa fa-exclamation-circle" /> {error}</div>}
        </ConsoleDialog>
    </>
}
